import React, { useMemo, useCallback, useReducer } from "react"
import { useFetcher } from "react-router"
import { LoaderCircleIcon, SaveIcon, Trash2Icon, PencilIcon, XCircleIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import type { EnhancedConfigData } from "./brand-lenses-configuration"
import type { EvidenceTheme } from "@/utils/types"

export interface SummaryThemesConfig {
  highlightColors?: { [themeName: string]: string }
  enableFilters?: boolean
  enableForUnknownClassifications?: boolean
  filters?: Array<{
    classification: string
    themes: Array<string | null>
  }>
  hiddenThemes?: { [themeName: string]: boolean }
}
interface FetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}

type AddedTheme = { theme: string; color: string }

type ThemeAction =
  | { type: "ADD"; theme: string; color: string }
  | { type: "EDIT"; theme: string; color: string }
  | { type: "REMOVE"; theme: string }
  | { type: "RESET" }

function themeReducer(state: AddedTheme[], action: ThemeAction): AddedTheme[] {
  switch (action.type) {
    case "ADD":
      // Prevent duplicates
      if (state.some((t) => t.theme === action.theme)) return state
      return [...state, { theme: action.theme, color: action.color }]

    case "EDIT":
      return state.map((t) => (t.theme === action.theme ? { ...t, color: action.color } : t))

    case "REMOVE":
      return state.filter((t) => t.theme !== action.theme)

    case "RESET":
      return []

    default:
      return state
  }
}

export function SummaryThemesTab({ data, config }: { data: EnhancedConfigData; config: SummaryThemesConfig }) {
  const fetcher = useFetcher<FetcherData>()
  const pendingAction = (fetcher.json as { action?: string } | undefined)?.action ?? null

  const allDialogueThemes = useMemo(() => {
    const themes = new Set(
      (data.evidenceThemeData ?? [])
        .filter((theme: EvidenceTheme) => theme.usage === "dialogue")
        .map((theme: EvidenceTheme) => theme.name),
    )

    return Array.from(themes).sort()
  }, [data.evidenceThemeData])

  const [addedThemes, dispatch] = useReducer(themeReducer, undefined, (): AddedTheme[] => {
    const raw = config.highlightColors
    if (raw == null) return []
    return Object.entries(raw).map(([theme, color]) => ({ theme, color: String(color) }))
  })

  const availableThemesForPicker = useMemo(() => {
    const addedThemesSet = new Set(addedThemes.map((a) => a.theme))
    return allDialogueThemes.filter((t) => !addedThemesSet.has(t))
  }, [allDialogueThemes, addedThemes])

  React.useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.success === true && fetcher.data.action === "REMOVE_SECTION") {
      dispatch({ type: "RESET" })
    }
  }, [fetcher.state, fetcher.data])

  const handleAddTheme = useCallback((theme: string, color: string) => {
    dispatch({ type: "ADD", theme, color })
  }, [])

  const handleRemoveTheme = useCallback((theme: string) => {
    dispatch({ type: "REMOVE", theme })
  }, [])

  const handleApplyEditedColor = useCallback((theme: string, newColor: string) => {
    dispatch({ type: "EDIT", theme, color: newColor })
  }, [])

  const handleSave = useCallback(
    (formEvent: React.FormEvent<HTMLFormElement>) => {
      formEvent.preventDefault()

      const value = {
        highlightColors: Object.fromEntries(addedThemes.map(({ theme, color }) => [theme, color])),
      }

      fetcher.submit(
        {
          action: "SAVE_SECTION",
          organisation: data.loadedOrganisation,
          project: data.loadedProject,
          jsonPath: "$.summaryThemes",
          value,
          concurrency: data.existingConfig?.concurrency ?? "",
        },
        { method: "post", encType: "application/json" },
      )
    },
    [fetcher, data, addedThemes],
  )

  const handleRemoveSection = useCallback(() => {
    fetcher.submit(
      {
        action: "REMOVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.summaryThemes",
        concurrency: data.existingConfig?.concurrency ?? "",
      },
      { method: "post", encType: "application/json" },
    )
  }, [fetcher, data])

  return (
    <div className="flex h-full flex-col">
      <fetcher.Form method="post" onSubmit={handleSave} className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-6">
            <div className="rounded-lg border border-info/20 bg-info/10 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-info">Summary Theme Colors</h3>
              </div>
              <p className="text-sm text-info/80">
                Set colors for evidence themes with &quot;Dialogue&quot; usage. These colors will be used in summary
                views.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium">Theme Colors</h3>
                <AddThemeDialog availableThemes={availableThemesForPicker} onAdd={handleAddTheme} />
              </div>

              {addedThemes.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-border py-12 text-center text-muted-foreground">
                  <p className="text-sm">No summary themes added</p>
                  <p className="mt-1 text-xs">Click Add to pick a dialogue theme</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {addedThemes.map(({ theme, color }) => (
                    <div key={theme} className="flex items-center justify-between gap-4 rounded-lg border bg-muted p-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-8 w-8 flex-shrink-0 rounded border border-input shadow-sm"
                          style={{ backgroundColor: color }}
                        />
                        <div>
                          <h4 className="font-medium text-foreground">{theme}</h4>
                          <p className="text-xs text-muted-foreground">{color}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <EditThemeDialog
                          theme={theme}
                          color={color}
                          onApply={(newColor) => handleApplyEditedColor(theme, newColor)}
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
                              <Trash2Icon className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent side="left" className="w-72">
                            <p className="text-sm">Remove theme &quot;{theme}&quot;?</p>
                            <div className="flex items-center justify-end gap-3 pt-4">
                              <PopoverClose asChild>
                                <Button type="button" variant="ghost" size="sm">
                                  Cancel
                                </Button>
                              </PopoverClose>
                              <PopoverClose asChild>
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleRemoveTheme(theme)}
                                >
                                  Remove
                                </Button>
                              </PopoverClose>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-t p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button type="submit" disabled={fetcher.state !== "idle"} className="flex-1 bg-info hover:bg-info/90">
              {pendingAction === "SAVE_SECTION" && fetcher.state !== "idle" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Summary Theme Colors
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={fetcher.state !== "idle"}
                >
                  {pendingAction === "REMOVE_SECTION" && fetcher.state !== "idle" ? (
                    <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircleIcon className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Summary Themes Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all summary theme configurations including colors.
                  </p>
                  <p className="text-sm font-medium text-foreground">This action cannot be undone.</p>

                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button type="button" variant="destructive" size="sm" onClick={() => void handleRemoveSection()}>
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </fetcher.Form>
    </div>
  )
}

function AddThemeDialog({
  availableThemes,
  onAdd,
}: {
  availableThemes: string[]
  onAdd: (theme: string, color: string) => void
}) {
  const [theme, setTheme] = React.useState("")
  const [color, setColor] = React.useState("#3b82f6")

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          setTheme("")
          setColor("#3b82f6")
        }
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" disabled={availableThemes.length === 0}>
          + Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Summary Theme</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Select Dialogue Theme</label>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className="w-full rounded border border-input bg-background p-2 text-sm text-foreground"
            >
              <option value="">-- Select a Theme --</option>
              {availableThemes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Choose Color</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-full cursor-pointer rounded border"
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button type="button" disabled={!theme} onClick={() => onAdd(theme, color)}>
              Add
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditThemeDialog({
  theme,
  color,
  onApply,
}: {
  theme: string
  color: string
  onApply: (newColor: string) => void
}) {
  const [draftColor, setDraftColor] = React.useState(color)

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) setDraftColor(color)
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="ghost" className="text-blue-600 hover:bg-info/10">
          <PencilIcon className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Color — {theme}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 flex-shrink-0 rounded border border-input shadow-sm"
              style={{ backgroundColor: draftColor }}
            />
            <span className="text-sm font-medium text-foreground">{theme}</span>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Choose Color</label>
            <input
              type="color"
              value={draftColor}
              onChange={(e) => setDraftColor(e.target.value)}
              className="h-10 w-full cursor-pointer rounded border"
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button type="button" onClick={() => onApply(draftColor)}>
              Apply
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
